import React, { useEffect } from 'react';
import toast from 'react-hot-toast';
import { useWebSocket } from '../../hooks/useWebSocket';
import {
  AlertTriangle as WarningIcon,
  AlertCircle as ErrorIcon,
  Info as InfoIcon,
  CheckCircle2 as SuccessIcon,
  X as CloseIcon,
} from 'lucide-react';
import { useNotificationSettings } from '../../hooks/useNotificationSettings';
import { sendPushNotification, canSendPush } from '../../services/notificationService';

const AlertToast = ({
  t,
  title,
  message,
  type,
}: {
  t: any;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success';
}) => {
  const getIcon = () => {
    switch (type) {
      case 'error':
        return <ErrorIcon className="text-red-500" size={24} />;
      case 'warning':
        return <WarningIcon className="text-yellow-500" size={24} />;
      case 'success':
        return <SuccessIcon className="text-green-500" size={24} />;
      default:
        return <InfoIcon className="text-cyan-500" size={24} />;
    }
  };

  const getBorderColor = () => {
    switch (type) {
      case 'error':
        return 'border-red-500/50';
      case 'warning':
        return 'border-yellow-500/50';
      case 'success':
        return 'border-green-500/50';
      default:
        return 'border-cyan-500/50';
    }
  };

  const getBgColor = () => {
    switch (type) {
      case 'error':
        return 'bg-red-50/90 dark:bg-red-950/90';
      case 'warning':
        return 'bg-yellow-50/90 dark:bg-yellow-950/90';
      case 'success':
        return 'bg-green-50/90 dark:bg-green-950/90';
      default:
        return 'bg-cyan-50/90 dark:bg-cyan-950/90';
    }
  };

  return (
    <div
      className={`${t.visible ? 'animate-enter' : 'animate-leave'} 
        max-w-md w-full shadow-lg rounded-2xl pointer-events-auto flex ring-1 ring-black ring-opacity-5 
        ${getBgColor()} backdrop-blur-md border ${getBorderColor()}
      `}
    >
      <div className="flex-1 w-0 p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0 pt-0.5">{getIcon()}</div>
          <div className="ml-3 flex-1">
            <p className="text-sm font-bold text-gray-900 dark:text-zinc-100">{title}</p>
            <p className="mt-1 text-sm text-gray-600 dark:text-zinc-300">{message}</p>
          </div>
        </div>
      </div>
      <div className="flex border-l border-gray-200 dark:border-zinc-700/50">
        <button
          onClick={() => toast.dismiss(t.id)}
          className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-gray-500 dark:text-zinc-400 hover:text-gray-700 dark:hover:text-zinc-200 focus:outline-none"
          title="Zavřít upozornění"
          aria-label="Zavřít upozornění"
        >
          <CloseIcon size={18} />
        </button>
      </div>
    </div>
  );
};

export const GlobalAlertListener: React.FC = () => {
  const { subscribe, on, off, isConnected } = useWebSocket();
  const { settings } = useNotificationSettings();

  useEffect(() => {
    // 0. Wait for connection
    if (!isConnected) return;

    // 1. Subscribe to alerts topic
    const unsubscribeFn = subscribe('alerts') as unknown as (() => void) | undefined;
    // console.log('GlobalAlertListener: Subscribed to alerts');

    // 2. Handler for incoming alerts
    const handleAlert = (message: any) => {
      // Logic duplicated from NotificationManager but optimized for Toast
      if (message.type === 'alert' || message.type === 'mqtt_message') {
        const payload = message.data || message.payload;
        if (!payload) return;

        const severity = payload.labels?.severity || 'info';
        const title = payload.labels?.alertname || 'System Alert';
        const description =
          payload.annotations?.description || payload.annotations?.summary || 'Unknown alert';

        let toastType: 'info' | 'warning' | 'error' | 'success' = 'info';
        let duration = 4000;

        if (severity === 'critical') {
          toastType = 'error';
          duration = 10000; // Longer for critical
        } else if (severity === 'warning' || severity === 'high') {
          toastType = 'warning';
          duration = 6000;
        } else if (severity === 'success') {
          // if resolved?
          toastType = 'success';
        }

        // Show Toast
        toast.custom(
          (t) => <AlertToast t={t} title={title} message={description} type={toastType} />,
          { duration, id: `alert-${payload.fingerprint || Date.now()}` },
        ); // Prevent duplicates with ID

        // Trigger Push Notification if applicable
        if (canSendPush(settings?.push_enabled)) {
          // Check severity/type criteria for push
          const isCritical = severity === 'critical';
          const isSensorThreshold = payload.labels?.alertname?.toLowerCase().includes('threshold');

          if (isCritical || isSensorThreshold) {
            sendPushNotification(title, {
              body: description,
              tag: `alert-${payload.fingerprint || 'generic'}`,
              requireInteraction: isCritical, // Keep critical on screen
            });
          }
        }
      }
    };

    // 3. Handler for System Logs (Errors)
    const handleSystemLog = (message: any) => {
      const payload = message.data;
      if (!payload) return;

      // Only toast on ERROR logs
      if (payload.level === 'ERROR' || payload.level === 'error') {
        toast.custom(
          (t) => (
            <AlertToast
              t={t}
              title={`System Error: ${payload.deviceId}`}
              message={payload.message}
              type="error"
            />
          ),
          { duration: 6000 },
        );
      }
    };

    on('alert', handleAlert);
    on('mqtt_message', handleAlert);
    on('system_log', handleSystemLog);

    return () => {
      off('alert', handleAlert);
      off('mqtt_message', handleAlert);
      off('system_log', handleSystemLog);
      if (unsubscribeFn) {
        try {
          unsubscribeFn();
        } catch (e) {
          // Ignore unsubscription errors if socket closed
        }
      }
    };
  }, [subscribe, on, off, isConnected]);

  return null; // Headless component
};
